

// components/employer/ReviewEmployeeModal.jsx   (FINAL – USES YOUR URL)
import { useState, useEffect } from "react";
import { X, User, Mail, Phone, Calendar, Star, Loader2, Clock, Briefcase } from "lucide-react";

export default function ReviewEmployeeModal({ onClose, onSuccess }) {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const currentUser = localStorage.getItem("userId") 

  const handleClose = () => {
  if (onClose) {
    onClose();
  } else {
    // Fallback: try to remove modal from DOM (not recommended long-term)
    const modal = document.querySelector('.fixed.inset-0.z-50');
    if (modal) modal.remove();
  }
};

  // FETCH EMPLOYEES USING YOUR EXACT URL
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!currentUser) {
        setFetchError("User not found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}employer-employee/${currentUser}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to load employees");

        const data = await response.json();
        const empList = Array.isArray(data) ? data : data.employees || [];
        setEmployees(empList);
      } catch (err) {
        setFetchError("Could not load employees");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [currentUser]);

  // RATING STATE
  const [ratings, setRatings] = useState({
    punctuality: 5,
    commitment: 5,
    hardwork: 5,
    dedication: 5
  });
  const [comment, setComment] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const totalScore = Object.values(ratings).reduce((a, b) => a + b, 0);

  const submitReview = async () => {
    if (!selectedEmployee) return alert("Please select an employee");

    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}employer-employee/review-employee`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("_dl_token")}`
        },
        body: JSON.stringify({
          jobSeekerId: selectedEmployee.jobSeeker._id || selectedEmployee._id,
          employeeRecordId: selectedEmployee._id,
          ...ratings,
          comment,
          isAnonymous
        })
      });

      const d = await res.json();
      alert(d.success ? "Review submitted successfully!" : d.message || "Failed");
      if (d.success) {
        onSuccess();
        onClose();
      }
    } catch (err) {
      alert("Network error");
    }
    setSubmitting(false);
  };

  // Rating Stars Component
  const RatingRow = ({ label, icon, value, onChange }) => (
    <div className="bg-white p-5 rounded-xl shadow border">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {icon}
          <span className="font-medium capitalize">{label}</span>
        </div>
        <span className="text-2xl font-bold text-purple-700">{value}/10</span>
      </div>
      <div className="flex gap-2 justify-center">
        {[1,2,3,4,5,6,7,8,9,10].map(n => (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={`w-10 h-10 rounded-full font-bold transition-all ${
              value >= n 
                ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-white shadow-lg" 
                : "bg-gray-200 text-gray-600 hover:bg-gray-300"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-4 max-h-screen overflow-y-auto">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-5 rounded-t-2xl z-10 flex justify-between items-center">
          <h2 className="text-xl md:text-2xl font-bold">Rate & Review Your Employee</h2>
          <button 
  onClick={handleClose}
  className="text-white hover:bg-white/20 p-2 rounded-full transition"
>
  <X className="w-6 h-6" />
</button>
          
        </div>

        <div className="p-6 md:p-8">
          {/* Loading / Error */}
          {loading && (
            <div className="text-center py-20">
              <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-4" />
              <p className="text-lg">Loading your employees...</p>
            </div>
          )}

          {fetchError && (
            <div className="text-center py-20 text-red-600">
              <p className="text-xl font-bold">{fetchError}</p>
            </div>
          )}

          {/* Employee Selection */}
          {!loading && !fetchError && employees.length === 0 && (
            <div className="text-center py-20">
              <User className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">No employees found</p>
            </div>
          )}

          {!loading && !fetchError && employees.length > 0 && !selectedEmployee && (
            <div>
              <h3 className="text-xl font-bold mb-6 text-center">Select Employee to Review</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-96 overflow-y-auto">
                {employees.map(emp => {
                  const seeker = emp.jobSeeker || emp;
                  return (
                    <div
                      key={emp._id}
                      onClick={() => setSelectedEmployee(emp)}
                      className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200 hover:border-purple-500 cursor-pointer transition shadow-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-purple-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold">
                          {seeker.firstName[0]}{seeker.lastName[0]}
                        </div>
                        <div>
                          <p className="font-bold text-lg">{seeker.firstName} {seeker.lastName}</p>
                          <p className="text-sm text-gray-600">{seeker.email}</p>
                          <p className="text-xs text-gray-500">Hired: {new Date(emp.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Review Form */}
          {selectedEmployee && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <p className="text-2xl font-bold">
                  Reviewing: {selectedEmployee.jobSeeker.firstName} {selectedEmployee.jobSeeker.lastName}
                </p>
                <button onClick={() => setSelectedEmployee(null)} className="text-purple-600 hover:underline text-sm">
                  ← Change employee
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <RatingRow label="Punctuality" icon={<Clock className="w-6 h-6 text-blue-600" />} value={ratings.punctuality} onChange={v => setRatings({...ratings, punctuality: v})} />
                <RatingRow label="Commitment" icon={<Star className="w-6 h-6 text-green-600" />} value={ratings.commitment} onChange={v => setRatings({...ratings, commitment: v})} />
                <RatingRow label="Hard Work" icon={<Briefcase className="w-6 h-6 text-purple-600" />} value={ratings.hardwork} onChange={v => setRatings({...ratings, hardwork: v})} />
                <RatingRow label="Dedication" icon={<User className="w-6 h-6 text-orange-600" />} value={ratings.dedication} onChange={v => setRatings({...ratings, dedication: v})} />
              </div>

              <div className="text-center py-6 bg-purple-50 rounded-2xl">
                <p className="text-5xl font-bold text-purple-700">{totalScore}/40</p>
                <p className="text-xl mt-2">
                  {totalScore >= 35 ? "Outstanding!" : totalScore >= 28 ? "Excellent" : totalScore >= 20 ? "Good" : "Needs Improvement"}
                </p>
              </div>

              <textarea
                placeholder="Your comment (optional)"
                rows="4"
                className="w-full p-4 border-2 rounded-xl focus:border-purple-500 text-base"
                value={comment}
                onChange={e => setComment(e.target.value)}
              />

              <label className="flex items-center gap-3">
                <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} className="w-5 h-5" />
                <span className="text-base">Submit anonymously</span>
              </label>

              <div className="flex gap-4">
                <button
                  onClick={submitReview}
                  disabled={submitting}
                  className="flex-1 bg-green-600 text-white py-4 rounded-xl font-bold hover:bg-green-700 disabled:opacity-70 flex items-center justify-center gap-3"
                >
                  {submitting ? <><Loader2 className="animate-spin w-6 h-6" /> Submitting...</> : "Submit Review"}
                </button>
                <button onClick={onClose} className="flex-1 bg-red-600 text-white py-4 rounded-xl font-bold hover:bg-red-700">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}






